/**
 * Public-page footer.
 *
 * Used on Landing, Pricing, Auth, Guides, Legal, and PublicReport. Mirrors the
 * primary nav so visitors who scrolled past the header can still reach pricing,
 * guides, and legal — and so the brand has a consistent close on every page.
 */
import { Link } from 'react-router-dom';
import { IGMark } from '@/components/IGMark';

interface FooterColumn {
  heading: string;
  links: Array<{ to: string; label: string; external?: boolean }>;
}

const COLUMNS: FooterColumn[] = [
  {
    heading: 'Product',
    links: [
      { to: '/', label: 'Home' },
      { to: '/solutions', label: 'Solutions' },
      { to: '/pricing', label: 'Pricing' },
      { to: '/guides', label: 'Guides' },
      { to: '/auth?mode=sign-up', label: 'Get started' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { to: '/guides/spf-setup', label: 'SPF setup' },
      { to: '/guides/dkim-setup', label: 'DKIM setup' },
      { to: '/guides/dmarc-rollout', label: 'DMARC rollout' },
      { to: '/guides/gmail-yahoo-bulk-sender-2026', label: 'Gmail/Yahoo 2026' },
    ],
  },
  {
    heading: 'Developers & agents',
    links: [
      { to: '/developers', label: 'Developers' },
      { to: '/developers#connect', label: 'MCP server' },
      { to: '/developers#install', label: 'Install guides' },
      { to: '/api-docs', label: 'API docs' },
      { to: '/openapi.json', label: 'API reference (OpenAPI)', external: true },
      { to: 'https://www.npmjs.com/package/inboxguard', label: 'npm SDK', external: true },
      { to: 'https://github.com/movaMedia-Inc/inboxguard-skills', label: 'Agent skills (GitHub)', external: true },
      { to: '/llms.txt', label: 'llms.txt', external: true },
      { to: '/agents.md', label: 'Agents guide', external: true },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { to: '/terms', label: 'Terms' },
      { to: '/privacy', label: 'Privacy' },
      { to: '/dpa', label: 'DPA' },
      { to: '/cookies', label: 'Cookies' },
      { to: '/acceptable-use', label: 'Acceptable use' },
    ],
  },
];

export function PublicFooter() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t bg-muted/20">
      <div className="container mx-auto px-4 py-12">
        <div className="grid gap-10 md:grid-cols-[2.4fr_repeat(4,1fr)]">
          <div className="md:pr-8">
            <Link to="/" className="flex items-center gap-2 text-xl font-semibold tracking-tight">
              <IGMark className="h-7 w-7 text-foreground" />
              InboxGuard
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Continuous SPF, DKIM, DMARC, MTA-STS, TLS-RPT, and 11-DNSBL monitoring with
              one-click DNS fixes. Built for engineers and deliverability teams who need to know
              their mail is landing in the inbox, not guess.
            </p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                {col.heading}
              </h3>
              <ul className="mt-4 space-y-2 text-sm">
                {col.links.map((l) =>
                  l.external ? (
                    <li key={l.to}>
                      <a
                        href={l.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {l.label}
                      </a>
                    </li>
                  ) : (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 border-t pt-6 text-center text-xs text-muted-foreground">
          © {year} InboxGuard. All rights reserved. From the labs of{' '}
          <a
            href="https://movamedia.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-foreground hover:underline"
          >
            movaMedia, Inc.
          </a>
        </div>
      </div>
    </footer>
  );
}

export default PublicFooter;
